import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Heart, Sprout, Briefcase, Users, Star, Quote } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { formatPrice } from "@/lib/format";

const HERO_IMG = "https://images.unsplash.com/photo-1506126613408-eca07ce68773?crop=entropy&cs=srgb&fm=jpg&q=85";
const ABOUT_IMG = "https://customer-assets.emergentagent.com/job_heart-mind-mentor/artifacts/eyr3t8l4_image.png";
const WORKSHOP_IMG = "https://images.unsplash.com/photo-1522075782449-e45a34f1ddfb?crop=entropy&cs=srgb&fm=jpg&q=85";
const FARM_IMG = "https://images.unsplash.com/photo-1763844599737-be3850c60cea?crop=entropy&cs=srgb&fm=jpg&q=85";
const COMMUNITY_IMG = "https://images.pexels.com/photos/7519289/pexels-photo-7519289.jpeg";

const CERTIFICATIONS = [
  "HYL Workshop Leader",
  "HYL Teens Teacher",
  "HYL Life Coach",
  "Business with Heart & Mind",
  "Law of Attraction Coach",
  "Soft Skills Trainer",
  "Natural Farming Teacher",
];

export default function Home() {
  const [programs, setPrograms] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [posts, setPosts] = useState([]);
  const [contact, setContact] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/programs").then((r) => setPrograms(r.data));
    api.get("/testimonials").then((r) => setTestimonials(r.data));
    api.get("/blog").then((r) => setPosts(r.data));
  }, []);

  const submitContact = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", contact);
      toast.success("Your note reached us. Anant will respond soon.");
      setContact({ name: "", email: "", subject: "", message: "" });
    } catch {
      toast.error("Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-20 md:pb-32 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="rise inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-ub bg-ub-alt text-sm">
              <Sparkles size={14} className="text-primary-ub" />
              <span className="text-ub-2">Transformation Catalyst · Life & Business Mentor</span>
            </div>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl tracking-tighter leading-[1.02] mt-6 rise-2">
              Heal what is,<br/>
              <span className="italic text-primary-ub">become</span> who you are.
            </h1>
            <p className="mt-6 text-lg md:text-xl text-ub-2 max-w-xl leading-relaxed rise-3">
              I'm <span className="font-medium text-ub">Anant Pandit</span> — a certified Heal Your Life® workshop
              leader, life coach, and business mentor. Together we release what no longer serves you and design a life
              rooted in truth, joy and purpose.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3 rise-4">
              <Link to="/programs" data-testid="hero-cta-programs" className="bg-primary-ub text-white rounded-full px-7 py-3.5 font-medium hover:bg-[var(--ub-primary-hover)] transition-colors inline-flex items-center gap-2">
                Explore Programs <ArrowRight size={16} />
              </Link>
              <Link to="/quiz" data-testid="hero-cta-quiz" className="rounded-full px-7 py-3.5 font-medium border border-ub-dark text-ub hover:bg-ub-dark hover:text-white transition-colors">
                Find Your Program
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5 rise-3">
            <div className="relative">
              <div className="absolute -top-6 -left-6 w-24 h-24 rounded-full bg-primary-soft blur-2xl opacity-70" />
              <img src={HERO_IMG} alt="Sunrise meditation" className="w-full h-[520px] object-cover rounded-[2rem] shadow-lg" />
              <div className="absolute -bottom-6 -right-6 bg-ub border border-ub rounded-2xl px-5 py-4 shadow-md">
                <div className="font-script text-primary-ub text-2xl leading-none">10,000+</div>
                <div className="text-xs uppercase tracking-widest text-muted-ub mt-1">Lives touched</div>
              </div>
            </div>
          </div>
        </div>
        {/* Marquee of certifications */}
        <div className="border-y border-ub bg-ub-alt overflow-hidden">
          <div className="py-5 marquee">
            {[...CERTIFICATIONS, ...CERTIFICATIONS].map((c, i) => (
              <span key={i} className="text-sm uppercase tracking-[0.2em] text-ub-2 flex items-center gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-primary-ub inline-block" /> {c}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-12 items-center" data-testid="about-section">
        <div className="md:col-span-5">
          <img src={ABOUT_IMG} alt="Anant Pandit" className="w-full h-[560px] object-cover rounded-[2rem]" />
        </div>
        <div className="md:col-span-7 md:pl-8">
          <div className="text-sm uppercase tracking-[0.25em] text-primary-ub">Meet Anant</div>
          <h2 className="font-serif text-4xl md:text-5xl mt-4 leading-tight">
            A quiet catalyst for<br/> the life you already sense.
          </h2>
          <p className="mt-6 text-lg text-ub-2 leading-relaxed">
            Anant weaves ancient wisdom with practical modern tools. Trained in Louise Hay's Heal Your Life® methodology,
            certified in Law of Attraction coaching, and a facilitator of "Leading Business with Heart and Mind" — he
            has guided founders, teens, professionals and farmers back to themselves.
          </p>
          <p className="mt-4 text-lg text-ub-2 leading-relaxed">
            His work is gentle but uncompromising: <span className="italic">only what is true will grow</span>.
          </p>
          <div className="mt-8 flex flex-wrap gap-2">
            {CERTIFICATIONS.map((c) => (
              <span key={c} className="text-xs uppercase tracking-widest border border-ub rounded-full px-3 py-1.5 text-ub-2">
                {c}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* PROGRAMS BENTO */}
      <section className="bg-ub-alt py-24 md:py-32" data-testid="programs-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
            <div>
              <div className="text-sm uppercase tracking-[0.25em] text-primary-ub">Programs & Workshops</div>
              <h2 className="font-serif text-4xl md:text-5xl mt-4 leading-tight max-w-2xl">
                Seven doorways.<br/> One journey home.
              </h2>
            </div>
            <Link to="/programs" className="text-ub-2 hover:text-primary-ub inline-flex items-center gap-2 text-sm uppercase tracking-widest">
              See all <ArrowRight size={14} />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            {programs.slice(0, 8).map((p, idx) => (
              <Link
                key={p.id}
                to={`/programs/${p.id}`}
                data-testid={`program-card-${p.id}`}
                className={`group relative overflow-hidden bg-ub border border-ub rounded-3xl p-8 hover:-translate-y-1 transition-all duration-500 hover:shadow-lg ${
                  idx === 0 ? "md:col-span-7 md:row-span-2" : idx === 1 ? "md:col-span-5" : "md:col-span-4"
                }`}
              >
                <div className="flex items-start justify-between">
                  <span className="text-xs uppercase tracking-widest text-primary-ub">{p.category}</span>
                  {p.price > 0 ? (
                    <span className="text-sm text-ub-2">{formatPrice(p.price, p.currency)}</span>
                  ) : (
                    <span className="text-sm text-secondary-ub">Free / Custom</span>
                  )}
                </div>
                <h3 className={`font-serif mt-4 leading-tight ${idx === 0 ? "text-4xl md:text-5xl" : "text-2xl md:text-3xl"}`}>
                  {p.title}
                </h3>
                <p className="text-ub-2 mt-3">{p.tagline}</p>
                {idx === 0 && <p className="text-ub-2 mt-4 max-w-md">{p.description}</p>}
                <div className="flex items-center gap-2 mt-6 text-sm text-ub group-hover:text-primary-ub transition-colors">
                  Learn more <ArrowRight size={14} />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* COMMUNITY */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-12 items-center" data-testid="community-section">
        <div className="md:col-span-6 md:order-2">
          <img src={COMMUNITY_IMG} alt="Community" className="w-full h-[460px] object-cover rounded-[2rem]" />
        </div>
        <div className="md:col-span-6">
          <div className="text-sm uppercase tracking-[0.25em] text-primary-ub">The Community</div>
          <h2 className="font-serif text-4xl md:text-5xl mt-4 leading-tight">
            Bloom in <span className="italic">good company</span>.
          </h2>
          <p className="mt-6 text-lg text-ub-2 leading-relaxed">
            Join a warm circle of seekers, founders, parents and farmers walking the path with you. Monthly calls,
            member-only practices, and a quiet space to be seen.
          </p>
          <ul className="mt-6 space-y-3 text-ub-2">
            <li className="flex items-center gap-3"><Heart size={16} className="text-primary-ub" /> Monthly live circle with Anant</li>
            <li className="flex items-center gap-3"><Sprout size={16} className="text-primary-ub" /> Guided meditations & practices</li>
            <li className="flex items-center gap-3"><Users size={16} className="text-primary-ub" /> Peer support & accountability</li>
            <li className="flex items-center gap-3"><Briefcase size={16} className="text-primary-ub" /> Alumni exclusive events</li>
          </ul>
          <Link to="/dashboard" data-testid="community-join-btn" className="inline-flex mt-8 items-center gap-2 bg-ub-dark text-white rounded-full px-7 py-3.5 hover:bg-primary-ub transition-colors">
            Join with Google <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-ub-dark text-white py-24 md:py-32 relative overflow-hidden" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-sm uppercase tracking-[0.25em] text-[var(--ub-primary-soft)]">Kind words</div>
          <h2 className="font-serif text-4xl md:text-5xl mt-4 leading-tight max-w-2xl">
            "Something shifted, and it hasn't shifted back."
          </h2>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.slice(0, 6).map((t) => (
              <div key={t.id} data-testid={`testimonial-${t.id}`} className="bg-white/5 backdrop-blur border border-white/10 rounded-3xl p-7">
                <Quote size={22} className="text-[var(--ub-primary)]" />
                <p className="mt-4 leading-relaxed text-white/90">{t.quote}</p>
                <div className="mt-6 flex items-center justify-between">
                  <div>
                    <div className="font-medium">{t.name}</div>
                    <div className="text-sm text-white/60">{t.role}</div>
                  </div>
                  <div className="flex gap-0.5">
                    {[...Array(t.rating)].map((_, i) => (
                      <Star key={i} size={14} className="fill-[var(--ub-primary)] text-[var(--ub-primary)]" />
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BLOG PREVIEW */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32" data-testid="blog-section">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div>
            <div className="text-sm uppercase tracking-[0.25em] text-primary-ub">From the Journal</div>
            <h2 className="font-serif text-4xl md:text-5xl mt-4 leading-tight">Slow reads for a full life.</h2>
          </div>
          <Link to="/blog" className="text-ub-2 hover:text-primary-ub inline-flex items-center gap-2 text-sm uppercase tracking-widest">All posts <ArrowRight size={14} /></Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {posts.map((p) => (
            <article key={p.id} data-testid={`blog-card-${p.id}`} className="group">
              <div className="overflow-hidden rounded-3xl">
                <img src={p.cover} alt={p.title} className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-700" />
              </div>
              <div className="mt-5">
                <div className="flex items-center gap-3 text-xs uppercase tracking-widest text-muted-ub">
                  <span>{p.tag}</span><span>·</span><span>{p.date}</span>
                </div>
                <h3 className="font-serif text-2xl mt-2 leading-snug group-hover:text-primary-ub transition-colors">{p.title}</h3>
                <p className="text-ub-2 mt-2">{p.excerpt}</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* CONTACT */}
      <section className="bg-ub-alt py-24 md:py-32" data-testid="contact-section">
        <div className="max-w-6xl mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="text-sm uppercase tracking-[0.25em] text-primary-ub">Say hello</div>
            <h2 className="font-serif text-4xl md:text-5xl mt-4 leading-tight">
              Write to us like you'd write to a <span className="italic">friend</span>.
            </h2>
            <p className="mt-6 text-ub-2">Whether you're curious about a workshop, or just want to be heard — this inbox is a soft place to land.</p>
            <img src={FARM_IMG} alt="Nature" className="mt-8 w-full h-56 object-cover rounded-3xl" />
          </div>
          <form onSubmit={submitContact} className="md:col-span-7 bg-ub border border-ub rounded-3xl p-8 md:p-10 space-y-4" data-testid="contact-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input required placeholder="Your name" value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} data-testid="contact-name" className="px-5 py-3.5 rounded-2xl bg-ub-alt border border-ub focus:border-[var(--ub-primary)] outline-none transition-colors" />
              <input required type="email" placeholder="Email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} data-testid="contact-email" className="px-5 py-3.5 rounded-2xl bg-ub-alt border border-ub focus:border-[var(--ub-primary)] outline-none transition-colors" />
            </div>
            <input placeholder="Subject" value={contact.subject} onChange={(e) => setContact({ ...contact, subject: e.target.value })} data-testid="contact-subject" className="w-full px-5 py-3.5 rounded-2xl bg-ub-alt border border-ub focus:border-[var(--ub-primary)] outline-none transition-colors" />
            <textarea required rows={6} placeholder="What's on your mind?" value={contact.message} onChange={(e) => setContact({ ...contact, message: e.target.value })} data-testid="contact-message" className="w-full px-5 py-3.5 rounded-2xl bg-ub-alt border border-ub focus:border-[var(--ub-primary)] outline-none transition-colors resize-none" />
            <button disabled={sending} data-testid="contact-submit-btn" className="bg-primary-ub text-white rounded-full px-8 py-3.5 font-medium hover:bg-[var(--ub-primary-hover)] transition-colors disabled:opacity-50">
              {sending ? "Sending…" : "Send message"}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
